import { Children, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const CISCO_GRADIENT = 'linear-gradient(90deg, #3070e7, #16bdeb 30%, #ff007f 75%, #ff9000)'

interface CarouselProps {
  children: ReactNode
  itemClassName?: string
  gap?: string
  theme?: 'dark' | 'light'
  fadeColor?: string
  className?: string
  trackClassName?: string
  ariaLabel?: string
  showDots?: boolean
  showProgressBar?: boolean
  hintLabel?: string
  accentColor?: string
  hoverGlow?: boolean
  activeIndex?: number
  onSlideChange?: (index: number) => void
  showArrows?: boolean
  showEdgeFade?: boolean
  snapAlign?: 'start' | 'center'
}

export default function Carousel({
  children,
  itemClassName = 'w-[82%] sm:w-[46%] lg:w-[31%] xl:w-[23%]',
  gap = 'gap-5',
  theme = 'dark',
  fadeColor,
  className = '',
  trackClassName = '',
  ariaLabel,
  showDots = false,
  showProgressBar = true,
  hintLabel = 'Drag or use the arrows to explore',
  accentColor,
  hoverGlow = true,
  activeIndex: controlledIndex,
  onSlideChange,
  showArrows = true,
  showEdgeFade = true,
  snapAlign = 'start',
}: CarouselProps) {
  const accent = accentColor || CISCO_GRADIENT
  const trackRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [visibleCount, setVisibleCount] = useState(1)
  const [scrollPct, setScrollPct] = useState(0)
  const [viewportFraction, setViewportFraction] = useState(1)
  const [hasInteracted, setHasInteracted] = useState(false)
  const nudgedRef = useRef(false)
  const isDragging = useRef(false)
  const dragMoved = useRef(false)
  const dragStartX = useRef(0)
  const dragScrollStart = useRef(0)

  const items = useMemo(() => Children.toArray(children), [children])
  const resolvedFade = fadeColor || (theme === 'dark' ? '#0b1929' : '#ffffff')

  const updateScrollState = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)

    const maxScroll = el.scrollWidth - el.clientWidth
    setScrollPct(maxScroll > 0 ? el.scrollLeft / maxScroll : 0)
    const fraction = el.scrollWidth > 0 ? Math.min(1, el.clientWidth / el.scrollWidth) : 1
    setViewportFraction(fraction)

    const kids = Array.from(el.children) as HTMLElement[]
    if (!kids.length) return

    const avgWidth = el.scrollWidth / kids.length
    setVisibleCount(Math.max(1, Math.round(el.clientWidth / avgWidth)))

    const center = el.scrollLeft + el.clientWidth / 2
    let closest = 0
    let closestDist = Infinity
    kids.forEach((child, i) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2
      const dist = Math.abs(childCenter - center)
      if (dist < closestDist) {
        closestDist = dist
        closest = i
      }
    })
    setActiveIndex((prev) => {
      if (prev !== closest) onSlideChange?.(closest)
      return closest
    })
  }, [onSlideChange])

  useEffect(() => {
    updateScrollState()
    const el = trackRef.current
    if (!el) return
    el.addEventListener('scroll', updateScrollState, { passive: true })
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      ro.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateScrollState, items.length])

  useEffect(() => {
    if (controlledIndex != null) return
    const el = trackRef.current
    if (!el || nudgedRef.current) return
    const timer = window.setTimeout(() => {
      if (!trackRef.current || el.scrollWidth <= el.clientWidth + 4) return
      nudgedRef.current = true
      el.scrollBy({ left: 56, behavior: 'smooth' })
      window.setTimeout(() => el.scrollBy({ left: -56, behavior: 'smooth' }), 550)
    }, 700)
    return () => window.clearTimeout(timer)
  }, [items.length, controlledIndex])

  const scrollByPage = (dir: 1 | -1) => {
    const el = trackRef.current
    if (!el) return
    setHasInteracted(true)
    const kids = Array.from(el.children) as HTMLElement[]
    const center = el.scrollLeft + el.clientWidth / 2
    let nearest = 0
    let nearestDist = Infinity
    kids.forEach((child, i) => {
      const dist = Math.abs(child.offsetLeft + child.offsetWidth / 2 - center)
      if (dist < nearestDist) {
        nearestDist = dist
        nearest = i
      }
    })
    const targetIndex = Math.min(kids.length - 1, Math.max(0, nearest + dir))
    const target = kids[targetIndex]
    if (!target) return
    el.scrollTo({ left: target.offsetLeft - (el.clientWidth - target.offsetWidth) / 2, behavior: 'smooth' })
  }

  const scrollToIndex = (i: number) => {
    const el = trackRef.current
    if (!el) return
    setHasInteracted(true)
    const child = el.children[i] as HTMLElement | undefined
    if (!child) return
    el.scrollTo({ left: child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2, behavior: 'smooth' })
  }

  useEffect(() => {
    if (controlledIndex == null) return
    const el = trackRef.current
    if (!el) return
    if (controlledIndex === activeIndex) return
    const child = el.children[controlledIndex] as HTMLElement | undefined
    if (!child) return
    el.scrollTo({ left: child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2, behavior: 'smooth' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledIndex])

  const onPointerDown = (e: React.PointerEvent) => {
    const el = trackRef.current
    if (!el) return
    if ((e.target as HTMLElement).closest('a, button, input, textarea, select, [role="button"]')) return
    isDragging.current = true
    dragMoved.current = false
    dragStartX.current = e.clientX
    dragScrollStart.current = el.scrollLeft
    el.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current || !trackRef.current) return
    const dx = e.clientX - dragStartX.current
    if (Math.abs(dx) > 4) {
      dragMoved.current = true
      setHasInteracted(true)
    }
    trackRef.current.scrollLeft = dragScrollStart.current - dx
  }
  const endDrag = () => {
    isDragging.current = false
  }
  const onClickCapture = (e: React.MouseEvent) => {
    if (dragMoved.current) {
      e.preventDefault()
      e.stopPropagation()
      dragMoved.current = false
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      scrollByPage(1)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      scrollByPage(-1)
    }
  }

  const arrowBase =
    'group relative overflow-hidden w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none'
  const arrowIdle =
    theme === 'dark'
      ? 'bg-[#0f2035]/95 border border-white/15 text-white shadow-[0_4px_18px_rgba(0,0,0,0.35)]'
      : 'bg-white border border-gray-200 text-gray-700 shadow-[0_4px_18px_rgba(0,0,0,0.12)]'

  const rangeStart = Math.min(activeIndex + 1, items.length)
  const rangeEnd = Math.min(items.length, rangeStart + Math.max(0, visibleCount - 1))

  return (
    <div className={`relative ${className}`} role="region" aria-roledescription="carousel" aria-label={ariaLabel}>
      {!hasInteracted && items.length > visibleCount && (
        <div
          className={`absolute -top-7 right-0 text-[11px] font-medium ${
            theme === 'dark' ? 'text-[#6b8299]' : 'text-gray-400'
          }`}
        >
          {hintLabel}
        </div>
      )}

      <div
        className="absolute left-0 top-0 bottom-0 w-10 sm:w-16 z-10 pointer-events-none transition-opacity duration-300"
        style={{
          background: `linear-gradient(to right, ${resolvedFade}, transparent)`,
          opacity: showEdgeFade && canScrollLeft ? 1 : 0,
        }}
      />
      <div
        className="absolute right-0 top-0 bottom-0 w-10 sm:w-16 z-10 pointer-events-none transition-opacity duration-300"
        style={{
          background: `linear-gradient(to left, ${resolvedFade}, transparent)`,
          opacity: showEdgeFade && canScrollRight ? 1 : 0,
        }}
      />

      <div
        ref={trackRef}
        role="list"
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={`flex ${gap} overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth cursor-grab active:cursor-grabbing select-none px-1 pt-6 pb-3 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
          theme === 'dark' ? 'focus-visible:ring-[#16bdeb] focus-visible:ring-offset-[#0b1929]' : 'focus-visible:ring-[#049fd9] focus-visible:ring-offset-white'
        } ${trackClassName}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
      >
        {items.map((child, i) => (
          <div
            key={i}
            role="listitem"
            className={`relative shrink-0 group/slide ${snapAlign === 'center' ? 'snap-center' : 'snap-start'} ${itemClassName}`}
          >
            {hoverGlow && (
              <div
                className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover/slide:opacity-50 blur-sm transition-opacity duration-500 pointer-events-none"
                style={{ background: accent }}
              />
            )}
            <div
              className={`relative h-full transition-all duration-300 group-hover/slide:-translate-y-0.5 ${
                !hoverGlow ? 'group-hover/slide:shadow-lg' : ''
              }`}
            >
              {child}
            </div>
          </div>
        ))}
      </div>

      {showArrows && (
        <>
          <button
            type="button"
            aria-label="Previous"
            onClick={() => scrollByPage(-1)}
            disabled={!canScrollLeft}
            className={`${arrowBase} ${arrowIdle} absolute top-1/2 -translate-y-1/2 z-20 left-1 sm:-left-5 hover:scale-110 hover:border-transparent hover:shadow-lg`}
          >
            <span
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: accent }}
            />
            <ChevronLeft className="relative z-10 w-5 h-5 transition-colors group-hover:text-white" />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={() => scrollByPage(1)}
            disabled={!canScrollRight}
            className={`${arrowBase} ${arrowIdle} absolute top-1/2 -translate-y-1/2 z-20 right-1 sm:-right-5 hover:scale-110 hover:border-transparent hover:shadow-lg`}
          >
            <span
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: accent }}
            />
            <ChevronRight className="relative z-10 w-5 h-5 transition-colors group-hover:text-white" />
          </button>
        </>
      )}

      {showDots && items.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-5">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollToIndex(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                activeIndex === i
                  ? 'w-7'
                  : theme === 'dark'
                  ? 'w-1.5 bg-white/20 hover:bg-white/40'
                  : 'w-1.5 bg-gray-300 hover:bg-gray-400'
              }`}
              style={activeIndex === i ? { background: accent } : undefined}
            />
          ))}
        </div>
      )}

      {showProgressBar && items.length > 1 && (
        <div className="flex items-center gap-3 mt-5 px-1">
          <div className={`relative flex-1 h-1.5 rounded-full overflow-hidden ${theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'}`}>
            <div
              className="absolute top-0 h-full rounded-full transition-[left,width] duration-200 ease-out"
              style={{
                background: accent,
                width: `${viewportFraction * 100}%`,
                left: `${scrollPct * (1 - viewportFraction) * 100}%`,
                boxShadow: accentColor ? undefined : '0 0 10px rgba(22,189,235,0.55)',
              }}
            />
          </div>
          <span className={`text-[11px] font-semibold tabular-nums whitespace-nowrap ${theme === 'dark' ? 'text-[#8eafc8]' : 'text-gray-500'}`}>
            {rangeStart === rangeEnd ? rangeStart : `${rangeStart}\u2013${rangeEnd}`} of {items.length}
          </span>
        </div>
      )}
    </div>
  )
}